import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Heart, Users, Globe, Sparkles, MessageCircle, Phone, Mail, MapPin, User } from 'lucide-react';
import '../styles/TherapyLanding.css';

const TherapyLanding = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, observerOptions);

    // Observe all elements with scroll animation classes
    const animatedElements = document.querySelectorAll(
      '.scroll-animate, .scroll-animate-left, .scroll-animate-right, .scroll-animate-scale, .stagger-item'
    );

    animatedElements.forEach((el) => observer.observe(el));

    return () => {
      animatedElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();

    // Store in localStorage for now (frontend-only)
    const submissions = JSON.parse(localStorage.getItem('contactSubmissions') || '[]');
    submissions.push({
      ...formData,
      timestamp: new Date().toISOString()
    });
    localStorage.setItem('contactSubmissions', JSON.stringify(submissions));

    // Create WhatsApp message
    const phoneNumber = '917550045659'; // +91 755-0045659 without spaces and special characters
    const message = `New Contact Form Submission\n\nName: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone}\n\nMessage:\n${formData.message}`;
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;

    // Open WhatsApp in new tab
    window.open(whatsappUrl, '_blank');

    alert('Redirecting to WhatsApp to send your message...');
    setFormData({ name: '', email: '', phone: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="therapy-landing">
      {/* Header */}
      <header className="header-nav">
        <div className="container header-content">
          <div className="logo-text heading-3 !font-normal">Nalam Therapy by Dhanya</div>
          <nav className="nav-links">
            <a href="#about" className="nav-link body-small">About</a>
            <a href="#approach" className="nav-link body-small">Approach</a>
            <a href="#contact" className="nav-link body-small">Contact</a>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="container hero-container">
          <div className="hero-content-wrapper">
            <div className="hero-image-container fade-in">
              <div className="profile-frame">
                <img
                  src="https://customer-assets.emergentagent.com/job_d505a930-f2e2-4e82-a705-5c481d16a61d/artifacts/rmr8o5y1_WhatsApp%20Image%202026-01-12%20at%2011.31.11%20AM.jpeg"
                  alt="Dhanya - Psychotherapist"
                  className="profile-image" />

              </div>
            </div>
            
            <div className="hero-text-content fade-in !bg-[rgba(255,255,255,0)]">
              <div className="hero-logo-container">
                <img
                  src="https://customer-assets.emergentagent.com/job_gentle-therapy-3/artifacts/vm8am6hu_06.Full%20Logo_Dark.png"
                  alt="Nalam Therapy by Dhanya"
                  className="hero-logo" />

              </div>
              
              <h1 className="heading-hero hero-title">Dhanya Murali</h1>
              <p className="heading-3 hero-subtitle-role">Counselling Psychologist</p>
              
              <p className="body-large hero-tagline">
                A warm collaborative space to feel heard, understood, and supported.
              </p>
              
              <div className="hero-cta-group">
                <a href="#contact" className="btn-primary !font-sans !font-bold !text-[#010101] !bg-[#F7F7F7]">Book a Consultation</a>
                <a href="#contact" className="btn-secondary !font-sans !font-bold !text-[#000000] !bg-[#FFFFFF]">CONTACT ME </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="section about-section">
        <div className="container">
          <div className="section-content !mt-[-11px] !mb-[-1px]">
            <h2 className="heading-1 section-title scroll-animate visible !mb-[111px]">About</h2>
            <div className="about-text body-large">
              <p className="scroll-animate visible !mt-[-100px] !mb-[-1px]">Hi, I’m Dhanya! I earned my Master’s degree from the University of San Diego and have been practicing as a therapist for over six years. I began my career in a group private practice before going on to establish my own practice in California. After recently relocating to India, I’m excited to begin this next chapter by building my private practice here as well! I am also licensed in California as a Licensed Professional Clinical Counselor (LPCC), and the training and clinical experiences I gained there continue to deeply inform my work today.
              </p>
              <p className="scroll-animate">
                My approach to therapy is warm, humanistic, and deeply relational. I believe therapy is, at its core, two real people sitting together and making sense of life’s challenges — you don’t have to navigate it alone. My priority is creating a space where you feel genuinely safe, comfortable, and truly heard. In this space, you are invited to share your story openly, explore your emotions with curiosity and compassion, and feel supported as you move toward meaningful change. I strive to create a thoughtful, collaborative process where we can work together to better understand your experiences and help you move forward with greater clarity and confidence. I especially enjoy working with teens and adults!
              </p>
              <p className="scroll-animate">
                Having lived much of my life between the United States and India, I bring a personal understanding of what it means to be far from home, adapt to new cultures, and navigate complex family and generational dynamics. These global experiences shape how I work with international clients, expats, and anyone moving between worlds — whether geographically, culturally, or emotionally.
              </p>
              <p className="scroll-animate">Outside of the therapy room, I enjoy cooking, staying active, trying new restaurants, watching TV shows, reading crime novels, traveling, and spending time with friends. I also love going to live music concerts with my husband!

              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Who I Work With */}
      <section className="section who-section">
        <div className="container">
          <h2 className="heading-1 section-title scroll-animate">Who I Work With</h2>
          <div className="who-grid">
            <Card className="who-card hover-lift stagger-item">
              <Users className="who-icon" size={32} />
              <h3 className="heading-3">Teens</h3>
              <p className="body-small">Supporting adolescents through developmental challenges and emotional growth</p>
            </Card>
            
            <Card className="who-card hover-lift stagger-item">
              <Sparkles className="who-icon" size={32} />
              <h3 className="heading-3">Young Adults</h3>
              <p className="body-small">Guiding young adults through life transitions, identity formation, and personal growth</p>
            </Card>
            
            <Card className="who-card hover-lift stagger-item">
              <User className="who-icon" size={32} />
              <h3 className="heading-3">Adults</h3>
              <p className="body-small">Working with individuals navigating life's complexities and transitions</p>
            </Card>
            
            <Card className="who-card hover-lift stagger-item">
              <Heart className="who-icon" size={32} />
              <h3 className="heading-3">Couples</h3>
              <p className="body-small">Supporting couples in strengthening relationships and improving communication</p>
            </Card>
            
            <Card className="who-card hover-lift stagger-item">
              <Globe className="who-icon" size={32} />
              <h3 className="heading-3">International Clients & Expats</h3>
              <p className="body-small">Understanding the unique experiences of living between cultures</p>
            </Card>
          </div>
          <p className="body-medium cultural-note scroll-animate">
            Cross-cultural sensitivity shaped by living between the United States and India.
          </p>
        </div>
      </section>

      {/* Therapeutic Style */}
      <section id="approach" className="section style-section">
        <div className="container">
          <h2 className="heading-1 section-title scroll-animate">Therapeutic Style</h2>
          <div className="style-grid scroll-animate-scale">
            <div className="style-item">
              <div className="style-dot"></div>
              <span className="body-medium">Humanistic</span>
            </div>
            <div className="style-item">
              <div className="style-dot"></div>
              <span className="body-medium">Relational</span>
            </div>
            <div className="style-item">
              <div className="style-dot"></div>
              <span className="body-medium">Collaborative</span>
            </div>
            <div className="style-item">
              <div className="style-dot"></div>
              <span className="body-medium">Emotion-focused</span>
            </div>
            <div className="style-item">
              <div className="style-dot"></div>
              <span className="body-medium">Culturally sensitive</span>
            </div>
            <div className="style-item">
              <div className="style-dot"></div>
              <span className="body-medium">CBT (Cognitive Behavioural Therapy)</span>
            </div>
            <div className="style-item">
              <div className="style-dot"></div>
              <span className="body-medium">Solution-Focused Therapy (SFT)</span>
            </div>
            <div className="style-item">
              <div className="style-dot"></div>
              <span className="body-medium">Trauma-Focused Therapy (TFT)</span>
            </div>
          </div>
        </div>
      </section>

      {/* Specializations */}
      <section className="section specializations-section">
        <div className="container">
          <h2 className="heading-1 section-title scroll-animate">Specializations</h2>
          <div className="spec-grid">
            <div className="spec-item body-medium stagger-item">Anxiety</div>
            <div className="spec-item body-medium stagger-item">Depression</div>
            <div className="spec-item body-medium stagger-item">LGBTQIA+</div>
            <div className="spec-item body-medium stagger-item">Trauma</div>
            <div className="spec-item body-medium stagger-item">School & academic stress</div>
            <div className="spec-item body-medium stagger-item">Self-esteem issues</div>
            <div className="spec-item body-medium stagger-item">Cross-cultural adjustment and transition</div>
            <div className="spec-item body-medium stagger-item">Workplace stress and burnout</div>
            <div className="spec-item body-medium stagger-item">Intergenerational and family-culture conflicts</div>
            <div className="spec-item body-medium stagger-item">Immigration and relocation-related stress</div>
            <div className="spec-item body-medium stagger-item">Bicultural and multicultural identity development</div>
            <div className="spec-item body-medium stagger-item">Relationship and interpersonal difficulties</div>
            <div className="spec-item body-medium stagger-item">Family & relational dynamics</div>
            <div className="spec-item body-medium stagger-item">Emotional regulation</div>
            <div className="spec-item body-medium stagger-item">Identity exploration and self-growth</div>
          </div>
        </div>
      </section>

      {/* Languages */}
      <section className="section languages-section">
        <div className="container">
          <h2 className="heading-1 section-title scroll-animate">Languages</h2>
          <div className="languages-list">
            <div className="language-badge body-medium stagger-item">English</div>
            <div className="language-badge body-medium stagger-item">Tamil</div>
            <div className="language-badge body-medium stagger-item">Telugu</div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section contact-section">
        <div className="container">
          <div className="contact-content">
            <div className="contact-intro">
              <h2 className="heading-1 section-title scroll-animate">Get in Touch</h2>
              <p className="body-large contact-reassurance scroll-animate">
                Reaching out can feel like a big step — you don't have to have the right words.
              </p>
              
              <p className="caption confidentiality-note scroll-animate">
                All communications are confidential. I typically respond within 24-48 hours.
              </p>
            </div>
            
            <Card className="contact-form-card scroll-animate-scale">
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="name" className="body-small">Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="form-input" />

                </div>
                
                <div className="form-group">
                  <label htmlFor="email" className="body-small">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input" />

                </div>
                
                <div className="form-group">
                  <label htmlFor="phone" className="body-small">Phone</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="form-input" />

                </div>
                
                <div className="form-group">
                  <label htmlFor="message" className="body-small">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    className="form-textarea" />

                </div>
                
                <Button type="submit" className="inline-flex items-center justify-center whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-primary/90 h-9 btn-primary form-submit !font-medium !text-sm gap-2 px-4 py-2 rounded-md shadow !text-[#000000] bg-primary">
                  <MessageCircle size={16} />
                  Send Message
                </Button>
              </form>
            </Card>
          </div>
        </div>
      </section>
    </div>);

};

export default TherapyLanding;
